import PropTypes from "prop-types";
import { useCallback, useContext, useEffect } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { fetchFicheProduit, fetchFicheProduitUri } from "app/pages/Listing/productActionCreators";
import { toggleAsideMenu } from "app/pages/Header/menuActionCreators";
import { clearBooking, updateBooking } from "app/pages/Booking/bookingActionCreators";
import { closeSlideshow, openSlideshow } from "app/pages/.shared/Slideshow/SlideShowActionCreators";
import { ficheProduitPropTypes } from "app/utils/propTypes";
import {
	FLAGSHIP_SCREENVIEW_KPIS,
	HTTP_STATUS_CODES,
	OFFER_CONTRACT_TYPES,
	OFFER_TYPES,
} from "app/constants";
import {
	getDesktopSlideShowPhotos,
	getSpecialOffer,
	getVideos,
} from "app/pages/FicheProduit/ficheProduitSelector";
import {
	getAllAvailableDurations,
	getAllNegociated,
	getAvailablePrepackageOffers,
} from "app/pages/PrePackage/searchEngineSelectors";
import { resetPrint, startPrint } from "app/reducers/printActionCreators";
import { saveNavLog } from "app/pages/Listing/NavLogs/navLogActionCreators";
import { useLocation, useMatch, useNavigate, useParams } from "react-router-dom";
import FicheProduit from "app/pages/FicheProduit/components/FicheProduit";
import { checkIfNavlogEnabled } from "app/pages/Listing/NavLogs/navLogsSelector";
import { isWhiteLabel } from "app/reducers/brandSelector";
import { updateCurrentMerchCode } from "app/pages/Merchandising/merchandisingActionCreators";
import { parseQueries } from "app/utils/routerUtils";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { getShowPfsMessage } from "app/reducers/partnerSelector";
import AppGlobalsContext from "app/AppGlobalsContext";
import { extractProductUriFromEndpoint } from "app/utils/urlDataExtractor";
import usePrintMode from "app/reducers/usePrintMode";
import { HitType, useFlagship } from "@flagship.io/react-sdk";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { getCalendarStressMarketingMessagesMapping } from "app/pages/StressMarketing/stressMarketingSelector";
import { fetchProductStressMarketingsData } from "app/pages/StressMarketing/stressMarketingActionCreators";
import { mapFicheProduitStoreToTag } from "app/utils/analytics-store-mapper";
import { mapProductResponseToState } from "app/utils/utils";

const FicheProduitContainer = props => {
	const {
		ficheProduit = {},
		fetchFicheProduit,
		fetchFicheProduitUri,
		updateBooking,
		clearBooking,
		saveNavLog,
		isNavlogsEnabled,
		closeSlideshow,
		resetPrint,
		updateCurrentMerchCode,
		resolution,
		startPrint,
		showTransportOfferOnly,
		// fetchProductStressMarketingsData = () => {},
	} = props;

	const { shop, productsVisibility } = useContext(AppGlobalsContext);

	const { search } = useLocation();
	const { fromMerch } = parseQueries(search);
	const { productId, productUri } = useParams();
	const isProductPageWithId = useMatch("/product/id/:productId");
	const navigate = useNavigate();
	const isDesktop = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;

	const { track } = useAnalytics();

	const isPrintMode = usePrintMode();

	const { sendHits } = useFlagship();

	useEffect(() => {
		sendHits([
			{
				type: HitType.PAGE_VIEW,
				documentLocation: window.location.origin + window.location.pathname,
			},
			{
				type: HitType.SCREEN_VIEW,
				documentLocation: FLAGSHIP_SCREENVIEW_KPIS.SCREENVIEW_PP_PRODUCT,
			},
		]);
	}, []);

	useEffect(() => {
		if (isPrintMode) {
			startPrint();
		}
	}, [isPrintMode]);

	// disabled for now because of a certificat issue FBR-3303
	// useEffect(() => {
	// 	if (ficheProduit?.geography?.destinationAirportIATA) {
	//fetchProductStressMarketingsData(ficheProduit?.geography?.destinationAirportIATA);
	// }
	// }, [ficheProduit]);

	const fetchFicheProduitData = useCallback(() => {
		if (isProductPageWithId) {
			fetchFicheProduitUri({
				productId: productId,
				shop,
				productsVisibility,
			}).then(res => {
				if (res.status === HTTP_STATUS_CODES.OK) {
					const productUri = extractProductUriFromEndpoint(res.data);
					navigate(`/${productUri}`);
				} else {
					track("fetch_product_json_fail", {
						productId: productId,
						httpStatus: res?.status,
					});

					// pas besoin de rediriger vers une technical error car le composant FicheProduit affiche le composant technical error si pas d'info fiche produit (voir ficheProduit.notFound)
				}
			});
		} else {
			fetchFicheProduit({
				productUri: productUri,
				productId: productId,
			}).then(res => {
				if (res.status === HTTP_STATUS_CODES.OK) {
					const offers = res?.data?.offers;

					if (offers?.length === 1 || showTransportOfferOnly) {
						const offer = offers[0];
						const offerType = offer.pricing.type;
						let offerToSave = {};
						let configToSave = undefined;

						if (offerType === OFFER_TYPES.ACCOMODATION_ONLY) {
							const { type, label, contractType, uri, config } = offer.pricing;
							offerToSave = {
								type,
								label,
								contractType,
								uri,
								negotiated: offer.negotiated,
							};
							configToSave = config;
						} else if (offerType === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION) {
							const { type, label, contractType, config } = offer.pricing;

							// Dans le cas d'un rental, l'offre AO doit toujours être sélectionné par défaut sinon
							// les durées ne s'affichent pas.
							// On fait un updateBooking ici à cause du FETCH_PRODUCTS_SUCCESS qui clear les selections du user
							if (contractType === OFFER_CONTRACT_TYPES.RENTAL) {
								offerToSave = {
									type: OFFER_TYPES.ACCOMODATION_ONLY,
									contractType: OFFER_CONTRACT_TYPES.RENTAL,
								};
							} else {
								offerToSave = {
									type,
									label,
									contractType,
									negotiated: offer.negotiated,
								};
								configToSave = config;
							}
						}

						updateBooking({
							config: configToSave,
							offer: offerToSave,
						});
					}

					if (isNavlogsEnabled) {
						saveNavLog({ product: { id: res?.data?.id } });
					}

					const productDataForAnalytics = mapProductResponseToState(res.data);

					track("product_page", {
						product: mapFicheProduitStoreToTag(productDataForAnalytics),
					});
				} else {
					track("fetch_product_json_fail", {
						productUri: productUri,
						httpStatus: res?.status,
					});

					// pas besoin de rediriger vers une technical error car le composant FicheProduit affiche le composant technical error si pas d'info fiche produit (voir ficheProduit.notFound)
				}
			});
		}
	}, [productUri, productId, isNavlogsEnabled]);

	useEffect(() => {
		// ficheProduit.notFound will be set server side
		// if true, it means that it is a not-found page with 404
		// so we avoid fetching the .json client side
		if (!ficheProduit.notFound) {
			fetchFicheProduitData();
		}

		return () => {
			closeSlideshow();
			resetPrint();
		};
	}, [ficheProduit.notFound]);

	useEffect(() => {
		const productUriFromUrl = productUri;
		const productIdFromUrl = productId;

		// Si le produit a changé, il faut réinitialiser ses choix dans le moteur de recherche sinon
		// il voit les prix d'un précédent produit
		// On regarde aussi l'id dans le cas où l'on passerait par la page /schizophrene
		// on peut vérifier ficheProduit.uri est non undefined pour ne pas clearBooking quand on vient de la sdp listing
		// sinon les infos de la barre de recherche ne seront plus persisté
		if (
			(productUriFromUrl && ficheProduit.uri !== productUriFromUrl) ||
			(productIdFromUrl && ficheProduit.id && ficheProduit.id !== productIdFromUrl)
		) {
			clearBooking();
		}
	}, [ficheProduit.uri, ficheProduit.id, productUri, productId]);

	useEffect(() => {
		if (isDesktop) {
			updateCurrentMerchCode(fromMerch);
		}
	}, [fromMerch, isDesktop]);

	return <FicheProduit {...props} />;
};

/* eslint react/forbid-prop-types:0 */
FicheProduitContainer.propTypes = {
	fetchFicheProduit: PropTypes.func,
	fetchFicheProduitUri: PropTypes.func,
	ficheProduit: ficheProduitPropTypes,
	toggleAsideMenu: PropTypes.func,
	slideshow: PropTypes.shape({
		isSlideshowOpened: PropTypes.bool,
		photosType: PropTypes.string,
	}),
	closeSlideshow: PropTypes.func,
	openSlideshow: PropTypes.func,
	saveNavLog: PropTypes.func,
	clearBooking: PropTypes.func,
	updateBooking: PropTypes.func,
	resetPrint: PropTypes.func,
	isNavlogsEnabled: PropTypes.bool,
	updateCurrentMerchCode: PropTypes.func,
	resolution: PropTypes.string,
	startPrint: PropTypes.func,
	showTransportOfferOnly: PropTypes.bool,
	fetchProductStressMarketingsData: PropTypes.func,
	stressMarketingMsgMapping: PropTypes.object,
};

const mapStateToProps = state => {
	return {
		resolution: state.resolution,
		ficheProduit: state.ficheProduit,
		currentShop: state?.shop,
		prePackageOffers: getAvailablePrepackageOffers(state),
		slideshow: state.slideshow,
		mobileSlideshowPhotos: state.ficheProduit.photos,
		desktopSlideShowPhotos: getDesktopSlideShowPhotos(state),
		videos: getVideos(state),
		isWhiteLabel: isWhiteLabel(state),
		negociated: getAllNegociated(state),
		allAvailableDurations: getAllAvailableDurations(state),
		partnerCode: state.partner.code,
		isNavlogsEnabled: checkIfNavlogEnabled(state),
		specialOffer: getSpecialOffer(state),
		shouldFetchLookAlikeProduts: state.partner.enableLookAlikeProducts,
		googleAPIKey: state.envVars.GOOGLE_API_KEY,
		showPfsMessage: getShowPfsMessage(state),
		productPriceValue: state.ficheProduit.fromPriceType?.value,
		showTransportOfferOnly: state.partner.showTransportOfferOnly,
		stressMarketingMsgMapping: getCalendarStressMarketingMessagesMapping(state),
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			fetchFicheProduit,
			fetchFicheProduitUri,
			toggleAsideMenu,
			closeSlideshow,
			openSlideshow,
			clearBooking,
			updateBooking,
			startPrint,
			resetPrint,
			saveNavLog,
			updateCurrentMerchCode,
			fetchProductStressMarketingsData,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(FicheProduitContainer);
